import { memo } from 'react';
import type { UseCreateTicketModalReturn } from './use-create-ticket-modal';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/_refactor/form/react-hook-form';
import { handleDomBubbleEvent } from '@/utils/dom';
import MessageError from '@/components/errors/message-error';
import { Calendar, Grid2x2X, MessagesSquare, Rocket, Tags, Users } from 'lucide-react';
import { Input } from '@/components/ui/_refactor/form/input';
import { formatDateRange } from '@/features/ticketings/tickets/ticket/utils/ticket.utils';
import MultiselectAutoComplete from '@/components/ui/_refactor/form/multiselect-autocomplete/multiselect-autocomplete';
import { Autocomplete } from '@/components/ui/_refactor/form/autocomplete';
import type { ICreateTicketModalProps } from './create-ticket-modal';
import {
  MemberOption,
  SelectedMemberItem,
} from '@/features/ticketing/tickets/components/modals/_shared/ticket-member-option';
import { CaseOption, SelectedCaseItem } from '@/features/ticketing/tickets/components/modals/_shared/case-option';
import {
  LabelOption,
  SelectedLabelItem,
} from '@/features/ticketing/tickets/components/modals/_shared/label-ticket-option';
import { AutoResizeTextarea } from '@/components/ui/_refactor/form/textareas';
import NewFileUpload from '@/features/_shared/components/file-handler/new-file-upload';
import { ModalConfirmButton, ModalContent, ModalFooter, ModalHeader } from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';
import { TColumn } from '@/features/ticketings/columns/column/types/column.type';
import { TSpace } from '@/features/ticketings/spaces/space/types/space.type';
import { cn } from '@/utils/cn';

type CreateTicketFormProps = ICreateTicketModalProps & UseCreateTicketModalReturn;

const DekstopCreateTicketForm = memo((props: CreateTicketFormProps) => {
  const {
    // Form
    triggerFrom,
    form,
    handleSubmitForm,
    isLoadingCreateTicket,

    // Date modal
    dateValues,
    handleOpenDateModal,

    // Spaces
    listSpacesOptions,
    isLoadingListSpacesData,

    // Space columns
    listSpaceColumnsOptions,
    isLoadingListSpaceColumnsData,

    // Space members
    listTicketMemberOptions,
    isLoadingListTicketMemberData,

    // Cases
    listCaseOptions,
    isLoadingListCaseData,

    // Label tickets
    listLabelTicketOptions,
    isLoadingListLabelTicketData,

    // Files
    extractedAttachments,
    uploadingFileIds,
    isUploadingFiles,
    handleAddFiles,
    handleRemoveFile,
  } = props;

  // RENDER
  return (
    <>
      <ModalHeader className="border-b border-[#D9D9D9]">
        <div className="flex items-center justify-between px-[16px] pt-[56px] pb-[12px]">
          <div className="flex flex-col gap-[8px]">
            <p className="text-[16px] font-[700] text-[#0C8DBF]">Create New Ticket</p>
            <p className="text-[12px] font-[400] text-[#9D9D9C]">
              Add a new ticket for your team. Set the title, date, member, and other details to keep work organized.
            </p>
          </div>
        </div>
      </ModalHeader>
      <ModalContent className="mt-[0px] py-[12px]">
        <Form {...form}>
          <div className="space-y-[16px] px-[16px]">
            <form
              id="add-ticketing-space-column-form"
              className="flex flex-col gap-[16px]"
              onSubmit={(e) => {
                handleDomBubbleEvent(e);
                form.handleSubmit(handleSubmitForm)(e);
              }}
            >
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => {
                  const error = form?.formState?.errors?.title?.message || '';
                  const value = field?.value || '';

                  return (
                    <FormItem>
                      <FormControl>
                        <input
                          {...field}
                          value={value}
                          placeholder="Enter a title for this card..."
                          type="text"
                          className="text-[#25151E] text-[24px] font-[700] w-full bg-transparent border-0 border-b-2 border-transparent focus:border-[#0C8DBF] focus:outline-none focus:ring-0 px-0 py-[0px] transition-colors duration-200 placeholder:text-[#9D9D9C] placeholder:font-[400]"
                        />
                      </FormControl>
                      <MessageError message={error} />
                    </FormItem>
                  );
                }}
              />
              {triggerFrom === 'case' && (
                <div className="grid grid-cols-2 gap-[40px]">
                  <div className="col-span-1">
                    <FormField
                      control={form.control}
                      name="spaceId"
                      render={({ field }) => {
                        const disabled = isLoadingListSpacesData;
                        const error = form?.formState?.errors?.spaceId?.message || '';
                        const value = listSpacesOptions?.find((option) => option.spaceId === field.value) || null;

                        return (
                          <FormItem>
                            <div className="flex items-center justify-between">
                              <FormLabel className="flex items-center gap-[8px]">
                                <Rocket className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                                <span className="text-[12px] font-[700] text-[#4B4B4B]">Space</span>
                              </FormLabel>
                              <div className="max-w-[378px] w-full">
                                <FormControl>
                                  <Autocomplete
                                    labelKey="name"
                                    valueKey="spaceId"
                                    placeholder="Select spaces"
                                    disabled={disabled}
                                    value={value}
                                    options={listSpacesOptions || []}
                                    isLoading={isLoadingListSpacesData}
                                    onChange={(selectedValue: TSpace | null) => {
                                      field.onChange(selectedValue?.spaceId || '');
                                    }}
                                    onClear={() => {
                                      form.setValue('spaceId', '', { shouldValidate: true });
                                      form.setValue('spaceColumnId', '', { shouldValidate: true });
                                    }}
                                  />
                                </FormControl>
                              </div>
                            </div>
                            <MessageError message={error} />
                          </FormItem>
                        );
                      }}
                    />
                  </div>
                  <div className="col-span-1">
                    <FormField
                      control={form.control}
                      name="spaceColumnId"
                      render={({ field }) => {
                        const disabled = !form.watch('spaceId') || isLoadingListSpaceColumnsData;
                        const error = form?.formState?.errors?.spaceColumnId?.message || '';
                        const value =
                          listSpaceColumnsOptions?.find((option) => option.spaceColumnId === field.value) || null;

                        return (
                          <FormItem>
                            <div className="flex items-center justify-between">
                              <FormLabel className="flex items-center gap-[8px]">
                                <Grid2x2X className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                                <span className="text-[12px] font-[700] text-[#4B4B4B]">Column</span>
                              </FormLabel>
                              <div className="max-w-[378px] w-full">
                                <FormControl>
                                  <Autocomplete
                                    labelKey="name"
                                    valueKey="spaceColumnId"
                                    placeholder="Select columns"
                                    value={value}
                                    disabled={disabled}
                                    isLoading={isLoadingListSpaceColumnsData}
                                    options={listSpaceColumnsOptions || []}
                                    onChange={(selectedValue: TColumn | null) => {
                                      field.onChange(selectedValue?.spaceColumnId ?? '');
                                    }}
                                    onClear={() => {
                                      form.setValue('spaceColumnId', '', { shouldValidate: true });
                                    }}
                                  />
                                </FormControl>
                              </div>
                            </div>
                            <MessageError message={error} />
                          </FormItem>
                        );
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-[40px]">
                <div className="col-span-1 flex flex-col gap-[10px]">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field: _field }) => {
                      const error =
                        form?.formState?.errors?.startDate?.message || form?.formState?.errors?.endDate?.message || '';

                      return (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel className="flex items-center gap-[8px]">
                              <Calendar className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                              <span className="text-[12px] font-[700] text-[#4B4B4B]">Dates</span>
                            </FormLabel>
                            <div className="max-w-[378px] w-full">
                              <FormControl>
                                <Input
                                  placeholder="Set Due date"
                                  value={formatDateRange(dateValues)}
                                  type="text"
                                  readOnly
                                  className="cursor-pointer"
                                  onClick={() => handleOpenDateModal()}
                                />
                              </FormControl>
                            </div>
                          </div>
                          <MessageError message={error} />
                        </FormItem>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="userIds"
                    render={({ field }) => {
                      const error = form?.formState?.errors?.userIds?.message || '';
                      const value =
                        listTicketMemberOptions?.filter((option) => field.value?.includes(option.userId)) || [];
                      const disabled = isLoadingListTicketMemberData;

                      return (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel className="flex items-center gap-[8px]">
                              <Users className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                              <span className="text-[12px] font-[700] text-[#4B4B4B]">Member</span>
                            </FormLabel>
                            <div className="max-w-[378px] w-full">
                              <FormControl>
                                <MultiselectAutoComplete
                                  labelKey="fullname"
                                  valueKey="userId"
                                  placeholder="Select members"
                                  searchPlaceholder="Search members"
                                  disabled={disabled}
                                  isLoading={isLoadingListTicketMemberData}
                                  options={listTicketMemberOptions || []}
                                  value={value}
                                  onChange={(value) => {
                                    field.onChange(value.map((option) => option.userId));
                                  }}
                                  onClearAll={() => {
                                    field.onChange([]);
                                  }}
                                  renderOption={(option) => <MemberOption option={option} />}
                                  renderSelectedItem={(option) => (
                                    <SelectedMemberItem
                                      option={option}
                                      onRemove={(option) => {
                                        if (option) {
                                          field.onChange(
                                            field.value?.filter((id: string) => id !== option.userId) || []
                                          );
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </FormControl>
                            </div>
                          </div>
                          <MessageError message={error} />
                        </FormItem>
                      );
                    }}
                  />
                </div>
                <div className="col-span-1 flex flex-col gap-[10px]">
                  <FormField
                    control={form.control}
                    name="caseIds"
                    render={({ field }) => {
                      const error = form?.formState?.errors?.caseIds?.message || '';
                      const value = listCaseOptions?.filter((option) => field.value?.includes(option.caseId)) || [];
                      const disabled = isLoadingListCaseData;

                      return (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel className="flex items-center gap-[8px]">
                              <MessagesSquare className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                              <span className="text-[12px] font-[700] text-[#4B4B4B]">Case</span>
                            </FormLabel>
                            <div className="max-w-[378px] w-full">
                              <FormControl>
                                <MultiselectAutoComplete
                                  labelKey="name"
                                  valueKey="caseId"
                                  placeholder="Select case"
                                  searchPlaceholder="Search case"
                                  options={listCaseOptions || []}
                                  value={value}
                                  disabled={disabled}
                                  isLoading={isLoadingListCaseData}
                                  onChange={(value) => {
                                    field.onChange(value.map((option) => option.caseId));
                                  }}
                                  onClearAll={() => {
                                    field.onChange([]);
                                  }}
                                  renderOption={(option) => <CaseOption option={option} />}
                                  renderSelectedItem={(option) => (
                                    <SelectedCaseItem
                                      option={option}
                                      onRemove={(option) => {
                                        if (option) {
                                          field.onChange(
                                            field.value?.filter((id: string) => id !== option.caseId) || []
                                          );
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </FormControl>
                            </div>
                          </div>
                          <MessageError message={error} />
                        </FormItem>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="labelTicketIds"
                    render={({ field }) => {
                      const error = form?.formState?.errors?.labelTicketIds?.message || '';
                      const value = listLabelTicketOptions?.filter((option) =>
                        field.value?.includes(option.labelTicketId)
                      );
                      const disabled = isLoadingListLabelTicketData;

                      return (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel className="flex items-center gap-[8px]">
                              <Tags className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                              <span className="text-[12px] font-[700] text-[#4B4B4B]">Label</span>
                            </FormLabel>
                            <div className="max-w-[378px] w-full">
                              <FormControl>
                                <MultiselectAutoComplete
                                  labelKey="name"
                                  valueKey="labelTicketId"
                                  placeholder="Select labels"
                                  searchPlaceholder="Search labels"
                                  disabled={disabled}
                                  isLoading={isLoadingListLabelTicketData}
                                  options={listLabelTicketOptions || []}
                                  value={value || []}
                                  onChange={(value) => {
                                    field.onChange(value.map((option) => option.labelTicketId));
                                  }}
                                  onClearAll={() => {
                                    field.onChange([]);
                                  }}
                                  renderOption={(option) => <LabelOption option={option} />}
                                  renderSelectedItem={(option) => (
                                    <SelectedLabelItem
                                      option={option}
                                      onRemove={(option) => {
                                        if (option) {
                                          field.onChange(
                                            field.value?.filter((id: string) => id !== option.labelTicketId) || []
                                          );
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </FormControl>
                            </div>
                          </div>
                          <MessageError message={error} />
                        </FormItem>
                      );
                    }}
                  />
                </div>
              </div>
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => {
                  const error = form?.formState?.errors?.content?.message || '';
                  const value = field?.value || '';

                  return (
                    <FormItem>
                      <div className="space-y-[8px]">
                        <FormLabel className="flex items-center gap-[8px]">
                          <span className="text-[12px] font-[700] text-[#4B4B4B]">Content</span>{' '}
                          <span className="text-[12px] font-[400] text-[#9D9D9C]">(Optional)</span>
                        </FormLabel>
                        <FormControl>
                          <AutoResizeTextarea
                            {...field}
                            value={value}
                            placeholder="Enter ticket details..."
                            minHeight={100}
                            className={cn(
                              'w-full rounded-[8px] border border-[#D9D9D9] p-[8px] text-[13px]',
                              error && 'border-red-500'
                            )}
                          />
                        </FormControl>
                      </div>
                      <MessageError message={error} />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="note"
                render={({ field }) => {
                  const error = form?.formState?.errors?.note?.message || '';
                  const value = field?.value || '';

                  return (
                    <FormItem>
                      <div className="space-y-[8px]">
                        <FormLabel className="flex items-center gap-[8px]">
                          <span className="text-[12px] font-[700] text-[#4B4B4B]">Note</span>{' '}
                          <span className="text-[12px] font-[400] text-[#9D9D9C]">(Optional)</span>
                        </FormLabel>
                        <FormControl>
                          <AutoResizeTextarea
                            {...field}
                            value={value}
                            placeholder="Enter ticket note..."
                            minHeight={100}
                            className={cn(
                              'w-full rounded-[8px] border border-[#D9D9D9] p-[8px] text-[13px]',
                              error && 'border-red-500'
                            )}
                          />
                        </FormControl>
                      </div>
                      <MessageError message={error} />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="attachments"
                render={({ field: _field }) => {
                  const error = form?.formState?.errors?.attachments?.message || '';

                  return (
                    <FormItem>
                      <div className="space-y-[8px]">
                        <FormLabel className="flex items-center gap-[8px]">
                          <span className="text-[12px] font-[700] text-[#4B4B4B]">Attachments</span>{' '}
                          <span className="text-[12px] font-[400] text-[#9D9D9C]">(Optional)</span>
                        </FormLabel>
                        <FormControl>
                          <NewFileUpload
                            accept="*"
                            maxSize={20 * 1024 * 1024} // 20MB
                            maxFiles={10}
                            initialFiles={extractedAttachments || []}
                            isUploadingFiles={isUploadingFiles}
                            uploadingFileIds={uploadingFileIds}
                            onFilesAdded={(files) => handleAddFiles(files)}
                            onFileRemoved={(removedFile) => handleRemoveFile(removedFile)}
                          />
                        </FormControl>
                      </div>
                      <MessageError message={error} />
                    </FormItem>
                  );
                }}
              />
            </form>
          </div>
        </Form>
      </ModalContent>
      <ModalFooter className="mt-0 p-[16px] py-[12px] border-t border-[#D9D9D9]">
        <ModalConfirmButton
          type="submit"
          form="add-ticketing-space-column-form"
          disabled={isLoadingCreateTicket || isUploadingFiles}
        >
          {isLoadingCreateTicket ? <Spinner className="w-[18px] h-[18px]" /> : null}
          Create
        </ModalConfirmButton>
      </ModalFooter>
    </>
  );
});
DekstopCreateTicketForm.displayName = 'DekstopCreateTicketForm';
export default DekstopCreateTicketForm;
